import { ReactNode } from 'react';
import { PartyPopper } from 'lucide-react';
import { useLang, Lang } from '../../contexts/LangContext';
import { trustLinks } from '../../lib/trust';

interface DrawShellProps {
  /** The organisation running the draw (shown in the festive header) */
  orgName?: string;
  /**
   * The organisation's public slug (its Trust Centre page, trust.pdpo.ai/p/
   * <slug>) and public portal token ("request your data" and "report a
   * concern" are addressed through it). The footer renders whichever links
   * these allow — a missing slug or token drops its link, never invents one.
   */
  orgSlug?: string | null;
  orgPortalToken?: string | null;
  children: ReactNode;
}

const LANGS: { value: Lang; label: string }[] = [
  { value: 'en', label: 'EN' },
  { value: 'ms', label: 'BM' },
];

/**
 * Public shell for draw.pdpo.ai pages — playful register, gold-tinted
 * festive header with the shop's name and an EN | BM toggle.
 */
export function DrawShell({ orgName, orgSlug, orgPortalToken, children }: DrawShellProps) {
  const { lang, setLang, t } = useLang();
  const links = trustLinks(orgSlug, orgPortalToken);
  const footerLinks = [
    links.url && {
      key: 'notice',
      href: links.url,
      label: t({ en: 'Privacy notice', ms: 'Notis privasi' }),
      title: orgName
        ? t({ en: `Privacy & your rights at ${orgName}`, ms: `Privasi & hak anda di ${orgName}` })
        : t({ en: 'Privacy & your rights', ms: 'Privasi & hak anda' }),
    },
    links.requestUrl && {
      key: 'request',
      href: links.requestUrl,
      label: t({ en: 'Request your data', ms: 'Minta data anda' }),
      title: t({
        en: 'Request a copy or correction of your data',
        ms: 'Minta salinan atau pembetulan data anda',
      }),
    },
    links.reportUrl && {
      key: 'report',
      href: links.reportUrl,
      label: t({ en: 'Report a concern', ms: 'Laporkan kebimbangan' }),
      title: t({
        en: 'Report a concern about how your data was handled',
        ms: 'Laporkan kebimbangan tentang cara data anda dikendalikan',
      }),
    },
  ].filter((l): l is { key: string; href: string; label: string; title: string } => Boolean(l));

  return (
    <div className="flex min-h-screen flex-col bg-paper">
      <header className="border-b border-gold/40 bg-gradient-to-b from-gold-tint to-paper">
        <div className="mx-auto flex max-w-md items-center justify-between gap-3 px-4 py-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gold/20">
              <PartyPopper className="h-5 w-5 text-gold" />
            </span>
            <span className="truncate font-display text-lg font-bold text-ink">
              {orgName ?? 'Lucky Draw'}
            </span>
          </div>
          <div
            className="flex flex-shrink-0 overflow-hidden rounded-full border border-ink/20"
            role="group"
            aria-label="Language"
          >
            {LANGS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => setLang(value)}
                aria-pressed={lang === value}
                className={`px-3 py-1 text-xs font-semibold transition-colors ${
                  lang === value ? 'bg-ink text-paper' : 'text-ink hover:bg-mist-100'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-md flex-1 px-4 py-6">{children}</main>

      <footer className="space-y-1 pb-6 pt-2 text-center text-xs text-mist-400">
        {footerLinks.length > 0 && (
          <p>
            {footerLinks.map((link, i) => (
              <span key={link.key}>
                {i > 0 && <span aria-hidden="true"> · </span>}
                <a
                  href={link.href}
                  title={link.title}
                  target="_blank"
                  rel="noreferrer"
                  className="underline decoration-mist-300 underline-offset-2 hover:text-ink"
                >
                  {link.label}
                </a>
              </span>
            ))}
          </p>
        )}
        <p>
          {t({ en: 'Powered by', ms: 'Dikuasakan oleh' })}{' '}
          <span className="font-semibold text-mist-500">PDPO Go</span> —{' '}
          {t({ en: 'fair draws, private data', ms: 'cabutan adil, data terlindung' })}
        </p>
      </footer>
    </div>
  );
}
